/**
 * The workspace catalog: a person's files across their conversations.
 *
 * Every thread owns one workspace; the catalog is the read-only view over
 * all of them for the acting person in one organization, grouped by the
 * conversation each file lives in. Bounds mirror the server's
 * (`apps/server/src/amoreg/workspaceCatalog`).
 */
import * as Schema from "effect/Schema";
import {
  IsoDateTime,
  NonNegativeInt,
  PositiveInt,
  ThreadId,
  TrimmedNonEmptyString,
  TrimmedString,
} from "./baseSchemas.ts";

export const WORKSPACE_CATALOG_MAX_QUERY_LENGTH = 256;
export const WORKSPACE_CATALOG_MAX_THREAD_LIMIT = 100;
export const WORKSPACE_CATALOG_MAX_FILE_LIMIT = 2_000;

export const WorkspaceCatalogListInput = Schema.Struct({
  organizationId: TrimmedNonEmptyString,
  /** Fuzzy, case-insensitive: query characters in order within the path; blank lists everything. */
  query: Schema.optional(
    TrimmedString.check(Schema.isMaxLength(WORKSPACE_CATALOG_MAX_QUERY_LENGTH)),
  ),
  threadLimit: Schema.optional(
    PositiveInt.check(Schema.isLessThanOrEqualTo(WORKSPACE_CATALOG_MAX_THREAD_LIMIT)),
  ),
  fileLimit: Schema.optional(
    PositiveInt.check(Schema.isLessThanOrEqualTo(WORKSPACE_CATALOG_MAX_FILE_LIMIT)),
  ),
  includeArchived: Schema.optional(Schema.Boolean),
  /** Leave one conversation out — typically the one being viewed, whose files are already on screen. */
  excludeThreadId: Schema.optional(ThreadId),
});
export type WorkspaceCatalogListInput = typeof WorkspaceCatalogListInput.Type;

export const WorkspaceCatalogFile = Schema.Struct({
  /** Workspace-relative, `/`-separated — the path the Files panel shows. */
  path: TrimmedNonEmptyString,
  size: NonNegativeInt,
  modifiedAt: IsoDateTime,
});
export type WorkspaceCatalogFile = typeof WorkspaceCatalogFile.Type;

export const WorkspaceCatalogAccess = Schema.Literals(["owner", "shared", "operator"]);
export type WorkspaceCatalogAccess = typeof WorkspaceCatalogAccess.Type;

export const WorkspaceCatalogThread = Schema.Struct({
  threadId: ThreadId,
  title: Schema.String,
  /** The web client's thread route, `/{org}/{thread}`. */
  href: TrimmedNonEmptyString,
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  access: WorkspaceCatalogAccess,
  /** Files in this conversation matching the query, before the file limit. */
  fileCount: NonNegativeInt,
  files: Schema.Array(WorkspaceCatalogFile),
  /** The conversation holds more files than the server will walk; `fileCount` is a lower bound. */
  listingTruncated: Schema.Boolean,
});
export type WorkspaceCatalogThread = typeof WorkspaceCatalogThread.Type;

export const WorkspaceCatalogListResult = Schema.Struct({
  threads: Schema.Array(WorkspaceCatalogThread),
  /** Conversations with at least one matching file, before the thread limit. */
  threadCount: NonNegativeInt,
  /** Matching files across those conversations, before the file limit. */
  fileCount: NonNegativeInt,
  truncated: Schema.Boolean,
});
export type WorkspaceCatalogListResult = typeof WorkspaceCatalogListResult.Type;
